<template>
  <!-- template for the modal component -->
  <transition name="modal">
    <div class="csp-modal-mask" @click.stop="$emit('close')">
      <div class="csp-modal-wrapper" @click.stop>
        <div class="csp-modal-container">
          <div class="create-session">
            <div class="header">
              <h3>Create a new session</h3>
            </div>
            <div class="content">
              <section class="create">
                <div class="label-container">
                  <span>Custom label:</span>
                  <input
                    ref="sessionLabel"
                    v-model="sessionLabel"
                    type="text"
                    maxlength="40"
                  />
                </div>
                <div class="settings-container">
                  <span>Model type:</span>
                  <a-dropdown
                    :disabled="templateSets.length <= 0"
                    :trigger="['click']"
                    placement="bottomLeft"
                  >
                    <Button
                      :disabled="templateSets.length <= 0"
                      class="dropdown-button"
                      @onClick="e => e.preventDefault()"
                    >
                      <font-awesome-icon icon="chevron-down" class="icon" />
                      <span v-if="!selectedTemplateByModel">Model</span>
                      <span v-else>{{ selectedTemplateByModel.model }}</span>
                    </Button>
                    <a-menu
                      @click="handleTemplateModelOptionClick"
                      class="menu-container"
                      slot="overlay"
                      mode="inline"
                    >
                      <a-menu-item
                        v-for="template in templateSets"
                        :key="template.id"
                        class="menu-item-container"
                      >
                        {{ template.model }}
                      </a-menu-item>
                    </a-menu>
                  </a-dropdown>
                </div>
                <div class="settings-container">
                  <span>Software edition:</span>
                  <a-dropdown
                    :disabled="filteredTemplates.length <= 0"
                    :trigger="['click']"
                    placement="bottomLeft"
                  >
                    <Button
                      :disabled="filteredTemplates.length <= 0"
                      class="dropdown-button"
                      @onClick="e => e.preventDefault()"
                    >
                      <font-awesome-icon icon="chevron-down" class="icon" />
                      <span v-if="!selectedTemplateBySWAndModel">Edition</span>
                      <span v-else>{{
                        selectedTemplateBySWAndModel.edition
                      }}</span>
                    </Button>
                    <a-menu
                      @click="handleTemplateEditionOptionClick"
                      class="menu-container"
                      slot="overlay"
                      mode="inline"
                    >
                      <a-menu-item
                        v-for="template in filteredTemplates"
                        :key="template.id"
                        class="menu-item-container-edition"
                      >
                        {{ template.edition }}
                      </a-menu-item>
                    </a-menu>
                  </a-dropdown>
                </div>
                <div class="settings-container">
                  <span>Auto shutdown (hours):</span>
                  <a-input-number
                    id="inputNumber"
                    :default-value="selectedShutdownTime"
                    v-model="selectedShutdownTime"
                    :min="0"
                    :max="24"
                    :parser="parseTimeout"
                    :formatter="formatTimeout"
                  />
                </div>

                <div class="settings-container">
                  <span>Use VPN tunnel</span>
                  <a-checkbox
                    class="vpn-checkbox"
                    :checked="useVPNSetup"
                    @change="onVPNSetupChange"
                  />
                </div>
                <span :disabled="selectedTemplateBySWAndModel === null"
                  >Price for new session:
                  {{
                    selectedTemplateBySWAndModel
                      ? selectedTemplateBySWAndModel.price
                      : "-"
                  }}
                  [credits/hour]
                </span>
              </section>
              <section class="actions">
                <span
                  >Registered sessions {{ sessions.length }}/{{
                    maxConcurrentSessions
                  }}</span
                >
                <div class="button-container">
                  <Button class="button" @onClick="$emit('close')"
                    >Cancel
                  </Button>
                  <Button
                    :disabled="isCreateButtonDisabled"
                    class="button"
                    @onClick="handleCreate"
                    >Create
                    {{
                      selectedTemplateBySWAndModel
                        ? selectedTemplateBySWAndModel.price + "/h"
                        : ""
                    }}</Button
                  >
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import Button from "@/components/Button.vue";
import { createVM } from "@/api/vm";
import { isMachineInActiveState } from "@/utils/vm";

export default {
  name: "CreateSession",
  components: { Button },
  props: {
    sessions: {
      type: Array,
      required: true,
      default: () => {
        return [];
      }
    },
    totalCreditsForThisMonth: Number,
    usedCreditsThisMonth: Number,
    maxConcurrentSessions: Number,
    templates: {
      type: Array,
      required: true,
      default: () => {
        return [];
      }
    }
  },
  data() {
    return {
      sessionLabel: "",
      useVPNSetup: true,
      isEditable: false,

      selectedTemplateByModel: null,
      selectedTemplateBySWAndModel: null,
      selectedShutdownTime: 1,
      processing: false,
      isSubmitting: false
    };
  },
  computed: {
    filteredTemplates() {
      if (!this.selectedTemplateByModel) {
        return [];
      }

      return this.templates
        .filter(
          template => template.model === this.selectedTemplateByModel.model
        )
        .sort((a, b) => {
          const ma = a.edition.toLowerCase();
          const mb = b.edition.toLowerCase();
          const regex = /\d\d\.\d\d/;
          if (ma.match(regex) && mb.match(regex)) {
            const mal = a.edition.toLowerCase().length;
            const mbl = b.edition.toLowerCase().length;
            const may = a.edition.split(".")[1].slice(0, 2);
            const mby = b.edition.split(".")[1].slice(0, 2);
            const mam = a.edition.split(".")[0].slice(0, 2);
            const mbm = b.edition.split(".")[0].slice(0, 2);
            return may > mby
              ? -1
              : may < mby
              ? 1
              : mam > mbm
              ? -1
              : mam < mbm
              ? 1
              : mal > mbl
              ? -1
              : mal < mbl
              ? 1
              : 0;
          }
          return ma > mb ? -1 : ma < mb ? 1 : 0;
        });
    },
    templateSets() {
      const map = {};
      const templates = [];
      this.templates.forEach(temp => {
        if (!map[temp.model]) {
          templates.push(temp);
          map[temp.model] = true;
        }
      });

      templates.sort((a, b) => {
        const ma = a.model.toLowerCase();
        const mb = b.model.toLowerCase();
        return ma < mb ? -1 : ma > mb ? 1 : 0;
      });
      return templates;
    },
    remainingCreditForThisMonth: function() {
      return (
        Number(this.totalCreditsForThisMonth || 0) -
        Number(this.usedCreditsThisMonth || 0)
      );
    },
    isCreateButtonDisabled: function() {
      return (
        !this.selectedTemplateByModel ||
        !this.selectedTemplateBySWAndModel ||
        this.processing ||
        this.isSubmitting ||
        this.selectedShutdownTime === -1 ||
        this.remainingCreditForThisMonth === 0
      );
    },
    activeSessionCount() {
      return this.sessions.filter(session =>
        isMachineInActiveState(session.machineStateValue)
      ).length;
    }
  },
  methods: {
    parseTimeout(string) {
      string = string.replace(/\sh/, "");
      if (isNaN(string)) {
        return 0;
      }
      return Number.parseInt(string);
    },
    formatTimeout(value) {
      if (isNaN(value) | (Number.parseInt(value) === 0)) {
        return "disabled";
      }
      return Number.parseInt(value) + " h";
    },
    onVPNSetupChange(e) {
      this.useVPNSetup = Boolean(e.target.checked);
    },
    handleTemplateModelOptionClick(item = {}) {
      const selectedItem = this.templates.find(
        template => template.id === item.key
      );
      this.selectedTemplateByModel = selectedItem;
      // use first sw-edition by default
      this.selectedTemplateBySWAndModel = this.filteredTemplates
        ? this.filteredTemplates[0]
        : null;
    },
    handleTemplateEditionOptionClick(item = {}) {
      const selectedItem = this.templates.find(
        template => template.id === item.key
      );
      this.selectedTemplateBySWAndModel = selectedItem;
    },
    async handleCreate() {
      try {
        if (!this.selectedTemplateByModel) {
          return;
        }
        if (this.sessions.length >= this.maxConcurrentSessions) {
          this.$notification.warning({
            message: `Cannot create more than ${this.maxConcurrentSessions} sessions`,
            description: `You're not allowed to create more than ${this.maxConcurrentSessions} sessions.`
          });
          return;
        }
        this.isSubmitting = true;
        this.processing = true;
        // we don't need the response
        await createVM({
          templateId: this.selectedTemplateBySWAndModel.id,
          timeoutHours: this.selectedShutdownTime,
          label: this.sessionLabel,
          useVPNSetup: this.useVPNSetup
        });
        this.selectedTemplateByModel = null;
        this.selectedShutdownTime = 0;
        this.sessionLabel = "";
        this.useVPNSetup = true;
        this.isEditable = false;
        // reload the sessions
        this.isSubmitting = false;
        this.processing = false;
        this.$emit("close");
        // Delay of 1.5 second to ensure that session reload includes the new session.
        setTimeout( () => this.$emit("refetchSessions"), 1500 );
      } catch (error) {
        console.log(error);
        this.isSubmitting = false;
        this.processing = false;
        let message = "Could not create VM";
        if (error && error.response && error.response.data) {
          message = error.response.data;
        }
        this.$notification.error({
          message: "Could not create VM",
          description: message
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.csp-modal-mask {
  position: fixed;
  z-index: 10;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: table;
  transition: opacity 0.3s ease;
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: 16px;
}

.csp-modal-wrapper {
  display: table-cell;
  vertical-align: middle;
  align-self: center;
}

.csp-modal-container {
  width: 500px;
  border-radius: 0.1875rem;
  margin: 0px auto;
  padding: 20px 30px;
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  transition: all 0.3s ease;
  font-family: Helvetica, Arial, sans-serif;
}

.create-session {
  width: 100%;
  background-color: $backgroundColor;
}
.header {
  height: 80px;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding-left: $p4;
  background-color: $white;

  & h3 {
    margin-top: 0;

    text-align: center;
    color: $accentColor;
    font-family: "Rotis Sans Serif W01", Calibri, Arial;
    font-size: 2.375rem;
  }
}

.vpn-tunnel-section {
  margin-top: $m1;
}

.content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0;
  background-color: $backgroundColor;
  width: 100%;
  padding-left: $p1;
  padding-right: $p1;
  min-height: 10vh;
  padding-top: $p2;

  & > section {
    margin: $m2;
    padding: $m2;
  }

  & .actions {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-end;
    flex-wrap: wrap;
    padding-bottom: 0;

    & > .progress {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      margin-top: $m1;
      margin-bottom: $m1;
    }

    & > span {
      font-size: $baseTextSize;
      color: $textColor;
      font-weight: 700;
    }

    & small {
      word-wrap: break-word;
      color: $textColor;
      text-align: right;
    }
    & > div {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: flex-end;

      & > .button {
        height: 40px;
        width: 150px;
        margin-top: $m1;
        margin-left: $m1;
        color: $white;
        font-size: $textSmall;
      }
    }
  }

  & .create {
    background: $white;
    display: flex;
    flex-direction: column;

    span[disabled="disabled"] {
      color: lightgray;
    }

    & .label-container {
      min-height: 45px;
      border-radius: 8px;
      padding-left: $p1;
      padding-right: $p1;
      justify-content: space-between;
      align-items: center;
      display: flex;

      & input {
        width: 200px;
        border-width: 1px;
      }
      & .icon {
        color: $accentColor;
        cursor: pointer;
      }

      & .input-container {
        display: flex;
        align-items: center;
        background: none;
        & > span {
          margin-right: $m1;
        }

        & > * {
          color: $textColor;
          font-weight: 700;
          font-size: $textSmall;
        }

        & > input {
          background: $white;
          border: none;
          padding-left: $m1;

          &:disabled {
            background: none;
          }
        }
      }
    }

    & > .settings-container {
      justify-content: space-between;
      align-items: center;
      display: flex;
      flex-wrap: no-wrap;
      min-height: 45px;
      padding-left: $p1;
      padding-right: $p1;

      & span {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        text-align: left;
      }

      & .dropdown-button {
        width: 200px;
        height: 30px;
        background: #556679;
        font-size: $textSmall;
        box-shadow: none;
        border-radius: 20px;
        display: flex;
        justify-content: flex-start;
        align-items: center;

        &:disabled {
          cursor: not-allowed;
          background: $defaultFocus;
        }
        &:hover:disabled {
          background: $defaultFocus;
        }

        & .icon {
          margin-left: $m1;
          margin-right: 6px;
          color: $white;
          height: 20px;
          width: 20px;
        }

        &:focus,
        &:hover {
          background: #556679;
        }
      }

      & .credit-label {
        height: 40px;
        background-color: #f1f1f1;
        display: flex;
        align-items: center;
        width: 120px;
        border-radius: 8px;
        justify-content: center;
        color: $textColor;
        font-size: $baseTextSize;
        font-weight: 400;
        margin-top: $m1;
        margin-right: $m2;
      }
    }
  }
}
</style>
